/**
 * API Service Layer for Article Affiliate Patroller
 * Handles all API communication with the backend
 */

import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG, ERROR_MESSAGES } from '../config/api';
import { amplifyAuthService } from '../auth/amplifyAuthService';
import type {
    Guideline,
    GuidelineViolationCategory,
    GuidelineClientsResponse,
    GuidelineVersionsResponse,
    GuidelineResponse,
    UpdateGuidelineRequest
} from '../types/guideline';
import type { ViolationExportFilterSummary } from '../utils/violationExportFilename';

// Types for API responses
export interface ApiResponse<T = any> {
    success?: boolean;
    message?: string;
    data?: T;
    [key: string]: any;
}

// Export API response types
export interface ViolationExportResponse {
    success: boolean;
    taskId: string;
    totalUrls: number;
    estimatedTime: string;
    message: string;
}

export interface ViolationExportProgressResponse {
    taskId: string;
    status: string;
    progress: {
        total: number;
        completed: number;
        failed: number;
        /** URLs currently being exported (backend may omit on older builds) */
        processing?: number;
        percentage: number;
    };
    currentUrl?: string;
    estimatedTimeRemaining?: string;
    startedAt: string;
    completedAt?: string;
    errorMessage?: string;
}

export interface ViolationExportDownloadResponse {
    success: boolean;
    downloadUrl: string;
    expiresAt: string;
    fileSize: number;
    fileName: string;
}

export interface PaginatedResponse<T> {
    data: T[];
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
        hasNext: boolean;
        hasPrevious: boolean;
    };
}

// Error handling
export class ApiError extends Error {
    constructor(
        message: string,
        public status?: number,
        public response?: any
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

// Base API client
class ApiClient {
    private baseURL: string;
    private inFlight: Map<string, Promise<any>> = new Map();
    private cache: Map<string, { time: number; data: any }> = new Map();
    private cacheTTLms = 2000; // 2s cache for GET requests

    constructor(baseURL: string = API_BASE_URL) {
        this.baseURL = baseURL;
    }

    private withTimeout(
        config: RequestInit,
        timeoutMs: number
    ): { config: RequestInit; cancel: () => void } {
        if (!timeoutMs || timeoutMs <= 0) {
            return { config, cancel: () => {} };
        }

        const controller = new AbortController();
        const existingSignal = config.signal;

        let onAbort: (() => void) | undefined;
        if (existingSignal) {
            if (existingSignal.aborted) {
                controller.abort();
            } else {
                onAbort = () => controller.abort();
                existingSignal.addEventListener('abort', onAbort, { once: true });
            }
        }

        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

        return {
            config: {
                ...config,
                signal: controller.signal,
            },
            cancel: () => {
                window.clearTimeout(timeoutId);
                if (existingSignal && onAbort) {
                    existingSignal.removeEventListener('abort', onAbort);
                }
            },
        };
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;

        // Get authentication token (cached, minimal Cognito calls)
        const authHeaders: Record<string, string> = await amplifyAuthService.getAuthHeader();

        const mergedHeaders: Record<string, any> = {
            ...(REQUEST_CONFIG.headers as any),
            ...authHeaders,
            ...(options.headers as any),
        };

        // If using FormData, let the browser set Content-Type with boundary.
        if (options.body instanceof FormData) {
            delete mergedHeaders['Content-Type'];
            delete mergedHeaders['content-type'];
        }

        const config: RequestInit = {
            ...REQUEST_CONFIG,
            ...options,
            headers: mergedHeaders,
        };

        const timeoutMs =
            typeof (REQUEST_CONFIG as any).timeout === 'number'
                ? (REQUEST_CONFIG as any).timeout
                : 0;

        const method = (config.method || 'GET').toUpperCase();
        const cacheKey = `${method}:${url}:${method === 'GET' ? '' : JSON.stringify(config.body || '')}`;

        // Any mutation can change data returned by GET endpoints.
        // Invalidate the short in-memory GET cache so subsequent refetches see fresh data.
        if (method !== 'GET') {
            this.cache.clear();
        }

        // Serve from short cache for GETs
        if (method === 'GET') {
            const cached = this.cache.get(cacheKey);
            if (cached && Date.now() - cached.time < this.cacheTTLms) {
                return cached.data as T;
            }
        }

        // De-duplicate in-flight identical requests
        if (this.inFlight.has(cacheKey)) {
            return this.inFlight.get(cacheKey) as Promise<T>;
        }

        const exec = (async () => {
            const { config: timedConfig, cancel } = this.withTimeout(
                config,
                timeoutMs
            );
            try {
                const response = await fetch(url, timedConfig);

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new ApiError(
                        errorData.message || ERROR_MESSAGES.SERVER_ERROR,
                        response.status,
                        errorData
                    );
                }

                const data = await response.json();
                if (method === 'GET') {
                    this.cache.set(cacheKey, { time: Date.now(), data });
                }
                return data;
            } finally {
                cancel();
            }
        })();

        this.inFlight.set(cacheKey, exec);

        try {
            const data = await exec;
            return data as T;
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }

            if ((error as any)?.name === 'AbortError') {
                throw new ApiError(ERROR_MESSAGES.TIMEOUT_ERROR);
            }

            if (error instanceof TypeError) {
                throw new ApiError(ERROR_MESSAGES.NETWORK_ERROR);
            }

            throw new ApiError(ERROR_MESSAGES.SERVER_ERROR);
        } finally {
            this.inFlight.delete(cacheKey);
        }
    }

    // HTTP Methods
    async get<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' });
    }

    async post<T>(endpoint: string, data?: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async put<T>(endpoint: string, data?: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'DELETE' });
    }

    /** Parse filename from Content-Disposition (RFC 5987 filename* supported). */
    private parseContentDispositionFilename(header: string | null): string | undefined {
        if (!header) return undefined;
        const starMatch = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
        if (starMatch?.[1]) {
            try {
                return decodeURIComponent(starMatch[1].trim());
            } catch {
                return starMatch[1].trim();
            }
        }
        const quotedMatch = /filename="([^"]+)"/i.exec(header);
        if (quotedMatch?.[1]) return quotedMatch[1];
        const plainMatch = /filename=([^;\n]+)/i.exec(header);
        if (plainMatch?.[1]) return plainMatch[1].trim().replace(/^"|"$/g, '');
        return undefined;
    }

    async getDownloadBlob(
        endpoint: string
    ): Promise<{ blob: Blob; filename?: string }> {
        const url = `${this.baseURL}${endpoint}`;
        const authHeaders: Record<string, string> = await amplifyAuthService.getAuthHeader();

        const { config: timedConfig, cancel } = this.withTimeout(
            {
                method: 'GET',
                headers: {
                    ...authHeaders,
                },
            },
            (REQUEST_CONFIG as any).timeout
        );
        try {
            const response = await fetch(url, timedConfig);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError(
                    errorData.message || ERROR_MESSAGES.SERVER_ERROR,
                    response.status,
                    errorData
                );
            }

            const filename = this.parseContentDispositionFilename(
                response.headers.get('Content-Disposition')
            );
            return { blob: await response.blob(), filename };
        } catch (error) {
            if ((error as any)?.name === 'AbortError') {
                throw new ApiError(ERROR_MESSAGES.TIMEOUT_ERROR);
            }
            throw error;
        } finally {
            cancel();
        }
    }

    // Fetch image as blob with authentication
    async getImageBlob(endpoint: string): Promise<Blob> {
        const url = `${this.baseURL}${endpoint}`;
        const authHeaders: Record<string, string> = await amplifyAuthService.getAuthHeader();

        const { config: timedConfig, cancel } = this.withTimeout(
            {
                method: 'GET',
                headers: {
                    ...authHeaders,
                },
            },
            (REQUEST_CONFIG as any).timeout
        );
        try {
            const response = await fetch(url, timedConfig);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError(
                    errorData.message || ERROR_MESSAGES.SERVER_ERROR,
                    response.status,
                    errorData
                );
            }

            return await response.blob();
        } catch (error) {
            if ((error as any)?.name === 'AbortError') {
                throw new ApiError(ERROR_MESSAGES.TIMEOUT_ERROR);
            }
            throw error;
        } finally {
            cancel();
        }
    }

    // File upload
    async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: formData,
            headers: {
                // Don't set Content-Type for FormData, let browser set it with boundary
            },
        });
    }

    async uploadForm<T>(endpoint: string, formData: FormData, method: 'POST' | 'PUT'): Promise<T> {
        return this.request<T>(endpoint, {
            method,
            body: formData,
            headers: {
                // Don't set Content-Type for FormData, let browser set it with boundary
            },
        });
    }
}

// Create API client instance
export const apiClient = new ApiClient();

// Dashboard API
export const dashboardApi = {
    getStats: () => apiClient.get(API_ENDPOINTS.DASHBOARD_STATS),
    getActivities: (limit?: number) =>
        apiClient.get(`${API_ENDPOINTS.DASHBOARD_ACTIVITIES}${limit ? `?limit=${limit}` : ''}`),
};

// Device mode for patrol jobs (personal_computer = desktop, smart_phone = mobile)
export type DeviceMode = 'personal_computer' | 'smart_phone';
export type SearchTimeRangeMode = 'none' | 'preset' | 'absolute';

// Upload API
export const uploadApi = {
    uploadUrls: (
        urls: string[],
        guidelineId: string,
        version: number,
        asp: string,
        operationCompany: string,
        media: string,
        jobName: string,
        enableSearch?: boolean,
        deviceMode: DeviceMode = 'personal_computer',
        searchTimeRangeMode?: SearchTimeRangeMode,
        searchDateRestrict?: string,
        searchDateFrom?: string,
        searchDateTo?: string
    ) =>
        apiClient.post(API_ENDPOINTS.UPLOAD_URLS, {
            urls,
            guideline_id: guidelineId,
            version,
            asp,
            operation_company: operationCompany,
            media,
            job_name: jobName,
            enable_search: enableSearch ?? false,
            device_mode: deviceMode,
            search_time_range_mode: searchTimeRangeMode,
            search_date_restrict: searchDateRestrict,
            search_date_from: searchDateFrom,
            search_date_to: searchDateTo,
        }),
};

// Jobs API
export const jobsApi = {
    getJobs: (
        page?: number,
        limit?: number,
        jobStatuses?: string[],
        timeFilter?: string,
        search?: string,
        client?: string,
        version?: number,
        asp?: string,
        media?: string,
        operation_company?: string,
        clients?: string[],
        versions?: number[],
        asps?: string[],
        media_names?: string[],
        operation_companies?: string[],
        created_by_ids?: string[],
        device_modes?: string[],
    ) => {
        const params = new URLSearchParams();
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());
        if (jobStatuses && jobStatuses.length > 0) {
            params.append('statuses', jobStatuses.join(','));
        }
        if (timeFilter) params.append('timeFilter', timeFilter);
        if (search) params.append('search', search);
        if (client) params.append('client', client);
        if (version !== undefined) params.append('version', version.toString());
        if (asp) params.append('asp', asp);
        if (media) params.append('media', media);
        if (operation_company) params.append('operation_company', operation_company);
        if (clients && clients.length > 0) params.append('clients', clients.join(','));
        if (versions && versions.length > 0) params.append('versions', versions.join(','));
        if (asps && asps.length > 0) params.append('asps', asps.join(','));
        if (media_names && media_names.length > 0) params.append('media_names', media_names.join(','));
        if (operation_companies && operation_companies.length > 0) params.append('operation_companies', operation_companies.join(','));
        if (created_by_ids && created_by_ids.length > 0) params.append('createdBy', created_by_ids.join(','));
        if (device_modes && device_modes.length > 0) params.append('device_modes', device_modes.join(','));
        return apiClient.get(`${API_ENDPOINTS.JOBS}?${params}`);
    },

    getFilterOptions: (search?: string) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        return apiClient.get(`${API_ENDPOINTS.JOBS}/filter-options?${params}`);
    },

    executeAction: (jobId: string, action: string) =>
        apiClient.post(API_ENDPOINTS.JOB_ACTIONS(jobId), { action }),

    getUrlProcessResults: (
        page?: number,
        limit?: number,
        status?: string,
        jobIds?: string[],
        statuses?: string[],
        search?: string,
        timeFilters?: string[],
        clients?: string[],
        versions?: number[],
        asps?: string[],
        mediaNames?: string[],
        operationCompanies?: string[],
        createdByIds?: string[],
        categories?: string[],
    ) => {
        const params = new URLSearchParams();
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());
        if (jobIds && jobIds.length > 0) params.append('job_ids', jobIds.join(','));
        if (status) params.append('status', status);
        if (statuses && statuses.length > 0) params.append('statuses', statuses.join(','));
        if (search && search.trim() !== '') params.append('search', search.trim());
        if (timeFilters && timeFilters.length > 0) params.append('time_filters', timeFilters.join(','));
        if (clients && clients.length > 0) params.append('clients', clients.join(','));
        if (versions && versions.length > 0) params.append('versions', versions.join(','));
        if (asps && asps.length > 0) params.append('asps', asps.join(','));
        if (mediaNames && mediaNames.length > 0) params.append('media_names', mediaNames.join(','));
        if (operationCompanies && operationCompanies.length > 0) params.append('operation_companies', operationCompanies.join(','));
        if (createdByIds && createdByIds.length > 0) params.append('created_by', createdByIds.join(','));
        if (categories && categories.length > 0) params.append('categories', categories.join(','));
        return apiClient.get(`${API_ENDPOINTS.URL_PROCESS_RESULTS}?${params}`);
    },

    getUrlErrorTraceback: (jobId: string, urlId: string) =>
        apiClient.get(API_ENDPOINTS.URL_ERROR_TRACEBACK(jobId, urlId)),
};

// LLM Usage API (monitoring dashboard)
export interface LlmUsageLogListItem {
    id: string;
    created_at: string;
    job_id: string | null;
    url_id: string | null;
    url: string | null;
    guideline_id: string | null;
    violation_category_id: string | null;
    rule_type: string | null;
    model: string;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    cached_tokens: number;
    reasoning_tokens: number;
    cost: number | null;
    html_tokens: number | null;
    rule_prompt_tokens: number | null;
}

export interface LlmUsagePaginationInfo {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
    has_next: boolean;
    has_previous: boolean;
}

export interface LlmUsageListResponse {
    success: boolean;
    items: LlmUsageLogListItem[];
    pagination: LlmUsagePaginationInfo | null;
}

export interface LlmUsageFilterOptionsResponse {
    success: boolean;
    models: string[];
    rule_types: string[];
    guideline_ids: string[];
}

export interface LlmUsageTimeseriesPoint {
    bucket: string;
    call_count: number;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    cached_tokens: number;
}

export interface LlmUsageTimeseriesResponse {
    success: boolean;
    points: LlmUsageTimeseriesPoint[];
}

export interface LlmUsageDetailResponse {
    success: boolean;
    item: LlmUsageLogListItem;
    prompt_text: string;
    response_text: string;
}

export interface LlmUsageListParams {
    page?: number;
    limit?: number;
    jobId?: string;
    urlId?: string;
    guidelineId?: string;
    ruleTypes?: string[];
    models?: string[];
    createdBy?: string[];
    createdAfter?: string; // ISO date
    createdBefore?: string; // ISO date
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

const buildLlmUsageParams = (params: LlmUsageListParams): URLSearchParams => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.jobId) query.append('jobId', params.jobId);
    if (params.urlId) query.append('urlId', params.urlId);
    if (params.guidelineId) query.append('guidelineId', params.guidelineId);
    if (params.ruleTypes && params.ruleTypes.length > 0) query.append('ruleTypes', params.ruleTypes.join(','));
    if (params.models && params.models.length > 0) query.append('models', params.models.join(','));
    if (params.createdBy && params.createdBy.length > 0) query.append('createdBy', params.createdBy.join(','));
    if (params.createdAfter) query.append('createdAfter', params.createdAfter);
    if (params.createdBefore) query.append('createdBefore', params.createdBefore);
    if (params.search) query.append('search', params.search);
    if (params.sortBy) query.append('sortBy', params.sortBy);
    if (params.sortOrder) query.append('sortOrder', params.sortOrder);
    return query;
};

export const llmUsageApi = {
    getList: (params: LlmUsageListParams = {}): Promise<LlmUsageListResponse> => {
        const query = buildLlmUsageParams(params);
        return apiClient.get<LlmUsageListResponse>(`${API_ENDPOINTS.LLM_USAGE}?${query}`);
    },

    getFilterOptions: (): Promise<LlmUsageFilterOptionsResponse> =>
        apiClient.get<LlmUsageFilterOptionsResponse>(API_ENDPOINTS.LLM_USAGE_FILTER_OPTIONS),

    getTimeseries: (
        bucket: 'hour' | 'day',
        params: Omit<LlmUsageListParams, 'page' | 'limit' | 'search'> = {}
    ): Promise<LlmUsageTimeseriesResponse> => {
        const query = buildLlmUsageParams(params);
        query.append('bucket', bucket);
        return apiClient.get<LlmUsageTimeseriesResponse>(`${API_ENDPOINTS.LLM_USAGE_TIMESERIES}?${query}`);
    },

    getById: (usageId: string): Promise<LlmUsageDetailResponse> =>
        apiClient.get<LlmUsageDetailResponse>(API_ENDPOINTS.LLM_USAGE_DETAIL(usageId)),
};

// Accuracy monitoring dashboard (issue #225)
export interface AccuracyMonitoringTimeseriesPoint {
    bucket: string;
    group_key: string | null;
    group_label: string | null;
    reviewed_url_count: number;
    fp_count: number;
    fn_count: number;
    wrong_bounding_box_count: number;
    violations_after_review_count: number;
}

export interface AccuracyMonitoringTimeseriesResponse {
    success: boolean;
    points: AccuracyMonitoringTimeseriesPoint[];
}

export interface AccuracyMonitoringJobRow {
    job_id: string;
    job_name: string | null;
    guideline_id: string;
    created_at: string | null;
    reviewed_url_count: number;
    fp_count: number;
    fn_count: number;
    wrong_bounding_box_count: number;
    violations_after_review_count: number;
}

export interface AccuracyMonitoringJobListResponse {
    success: boolean;
    items: AccuracyMonitoringJobRow[];
    pagination: LlmUsagePaginationInfo | null;
}

export interface AccuracyMonitoringParams {
    page?: number;
    limit?: number;
    guidelineId?: string;
    createdAfter?: string; // ISO date
    createdBefore?: string; // ISO date
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    bucket?: 'day' | 'week' | 'month';
    divideBy?: 'none' | 'guideline' | 'category';
}

const buildAccuracyMonitoringParams = (params: AccuracyMonitoringParams): URLSearchParams => {
    const query = new URLSearchParams();
    if (params.page) query.append('page', params.page.toString());
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.guidelineId) query.append('guidelineId', params.guidelineId);
    if (params.createdAfter) query.append('createdAfter', params.createdAfter);
    if (params.createdBefore) query.append('createdBefore', params.createdBefore);
    if (params.sortBy) query.append('sortBy', params.sortBy);
    if (params.sortOrder) query.append('sortOrder', params.sortOrder);
    if (params.divideBy && params.divideBy !== 'none') query.append('divideBy', params.divideBy);
    return query;
};

export const accuracyMonitoringApi = {
    getTimeseries: (
        params: Omit<AccuracyMonitoringParams, 'page' | 'limit' | 'sortBy' | 'sortOrder'> = {}
    ): Promise<AccuracyMonitoringTimeseriesResponse> => {
        const query = buildAccuracyMonitoringParams(params);
        query.append('bucket', params.bucket || 'month');
        return apiClient.get<AccuracyMonitoringTimeseriesResponse>(
            `${API_ENDPOINTS.ACCURACY_MONITORING_TIMESERIES}?${query}`
        );
    },

    getJobs: (params: AccuracyMonitoringParams = {}): Promise<AccuracyMonitoringJobListResponse> => {
        const query = buildAccuracyMonitoringParams(params);
        return apiClient.get<AccuracyMonitoringJobListResponse>(
            `${API_ENDPOINTS.ACCURACY_MONITORING_JOBS}?${query}`
        );
    },

    exportViolationList: (
        params: Omit<AccuracyMonitoringParams, 'page' | 'limit' | 'sortBy' | 'sortOrder' | 'bucket' | 'divideBy'> = {}
    ): Promise<ViolationExportResponse> => {
        const query = buildAccuracyMonitoringParams(params);
        return apiClient.post<ViolationExportResponse>(
            `${API_ENDPOINTS.ACCURACY_MONITORING_EXPORT}?${query}`
        );
    },
};

// Page status dashboard (error / no-ad-notation URL counts by month)
export interface StatusDashboardTimeseriesPoint {
    bucket: string;
    total_url_count: number;
    error_count: number;
    no_ad_notation_count: number;
}

export interface StatusDashboardTimeseriesResponse {
    success: boolean;
    points: StatusDashboardTimeseriesPoint[];
}

export interface StatusDashboardParams {
    guidelineId?: string;
    createdById?: string;
    createdAfter?: string; // ISO date
    createdBefore?: string; // ISO date
}

export const statusDashboardApi = {
    getTimeseries: (params: StatusDashboardParams = {}): Promise<StatusDashboardTimeseriesResponse> => {
        const query = new URLSearchParams();
        if (params.guidelineId) query.append('guidelineId', params.guidelineId);
        if (params.createdById) query.append('createdById', params.createdById);
        if (params.createdAfter) query.append('createdAfter', params.createdAfter);
        if (params.createdBefore) query.append('createdBefore', params.createdBefore);
        return apiClient.get<StatusDashboardTimeseriesResponse>(
            `${API_ENDPOINTS.STATUS_DASHBOARD_TIMESERIES}?${query}`
        );
    },
};

// Violation trend dashboard (patrolled/violation URL counts and rate over time)
export interface ViolationTrendTimeseriesPoint {
    bucket: string;
    patrolled_url_count: number;
    violation_url_count: number;
    violation_count: number;
    violation_site_rate: number | null;
}

export interface ViolationTrendTimeseriesResponse {
    success: boolean;
    points: ViolationTrendTimeseriesPoint[];
}

export interface ViolationTrendParams {
    bucket?: 'month' | 'week';
    guidelineId?: string;
    createdAfter?: string; // ISO date
    createdBefore?: string; // ISO date
}

export const violationTrendApi = {
    getTimeseries: (params: ViolationTrendParams = {}): Promise<ViolationTrendTimeseriesResponse> => {
        const query = new URLSearchParams();
        query.append('bucket', params.bucket || 'month');
        if (params.guidelineId) query.append('guidelineId', params.guidelineId);
        if (params.createdAfter) query.append('createdAfter', params.createdAfter);
        if (params.createdBefore) query.append('createdBefore', params.createdBefore);
        return apiClient.get<ViolationTrendTimeseriesResponse>(
            `${API_ENDPOINTS.VIOLATION_TREND_TIMESERIES}?${query}`
        );
    },
};

// Violations API
export const violationsApi = {
    getViolations: (page?: number, limit?: number, status?: string, severity?: string, search?: string, jobId?: string, url?: string) => {
        const params = new URLSearchParams();
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());
        if (status) params.append('status', status);
        if (severity) params.append('severity', severity);
        if (search) params.append('search', search);
        if (jobId && jobId.trim() !== '') params.append('jobId', jobId);
        if (url && url.trim() !== '') params.append('url', url);
        const url_param = `${API_ENDPOINTS.VIOLATIONS}?${params}`;
        return apiClient.get(url_param);
    },

    getViolation: (violationId: string) => apiClient.get(API_ENDPOINTS.VIOLATION_DETAILS(violationId)),

    updateStatus: (violationId: string, status: string, notes?: string) =>
        apiClient.put(API_ENDPOINTS.UPDATE_VIOLATION_STATUS(violationId), { status, notes }),

    getStats: () => apiClient.get(API_ENDPOINTS.VIOLATION_STATS),

    // Export functions
    exportBatch: (
        urls: any[],
        options: {
            includeViolations: boolean;
            format?: string;
            filterSummary?: ViolationExportFilterSummary;
        }
    ) =>
        apiClient.post<ViolationExportResponse>(API_ENDPOINTS.EXPORT_VIOLATIONS_BATCH, { urls, options }),

    getExportProgress: (taskId: string) =>
        apiClient.get<ViolationExportProgressResponse>(API_ENDPOINTS.EXPORT_VIOLATIONS_PROGRESS(taskId)),

    getExportDownload: (taskId: string) =>
        apiClient.get<ViolationExportDownloadResponse>(API_ENDPOINTS.EXPORT_VIOLATIONS_DOWNLOAD(taskId)),
    cancelExport: (taskId: string) =>
        apiClient.post<{ success: boolean; message: string }>(API_ENDPOINTS.EXPORT_VIOLATIONS_CANCEL(taskId)),
    downloadExportFile: async (downloadUrl: string): Promise<Blob> => {
        const session = await amplifyAuthService.getCurrentSession();
        if (!session || !session.tokens.accessToken) {
            throw new Error('No valid session found');
        }

        // Remove /api/v1 from API_BASE_URL since downloadUrl already includes it
        const baseUrl = API_BASE_URL.replace('/api/v1', '');
        const response = await fetch(`${baseUrl}${downloadUrl}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${session.tokens.idToken}`,
                'Content-Type': 'application/zip'
            }
        });

        if (!response.ok) {
            throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }

        return await response.blob();
    },
};

// Violation API
export const violationApi = {
    getViolations: (jobId: string, urlId: string) =>
        apiClient.get(API_ENDPOINTS.VIOLATIONS_FOR_REVIEW(jobId, urlId)),

    createViolation: (jobId: string, urlId: string, violation: any) =>
        apiClient.post(API_ENDPOINTS.CREATE_VIOLATION(jobId, urlId), violation),

    updateViolation: (jobId: string, urlId: string, violationId: string, updates: any) =>
        apiClient.put(API_ENDPOINTS.UPDATE_VIOLATION(jobId, urlId, violationId), updates),

    deleteViolation: (jobId: string, urlId: string, violationId: string) =>
        apiClient.delete(API_ENDPOINTS.DELETE_VIOLATION(jobId, urlId, violationId)),

    bulkSaveViolations: (jobId: string, urlId: string, violations: any[], category_content_existence_dict?: Record<string, boolean>) =>
        apiClient.post(API_ENDPOINTS.BULK_SAVE_VIOLATIONS(jobId, urlId), { violations, category_content_existence_dict }),

    markReviewed: (jobId: string, urlId: string, notes?: string) =>
        apiClient.post(API_ENDPOINTS.MARK_REVIEWED(jobId, urlId), { notes }),

    exportPdf: (jobId: string, urlId: string, includeViolations?: boolean, format?: string) => {
        const params = new URLSearchParams();
        if (includeViolations !== undefined) params.append('includeViolations', includeViolations.toString());
        if (format) params.append('format', format);
        return apiClient.get(`${API_ENDPOINTS.EXPORT_PDF(jobId, urlId)}?${params}`);
    },

    // Get PNG URL for a specific job and URL ID
    getPngUrl: (jobId: string, urlId: string) => {
        return apiClient.get(API_ENDPOINTS.VIOLATIONS_FOR_REVIEW(jobId, urlId))
            .then((response: any) => {
                // Extract PNG URL from the response
                return response.pngUrl || response.png_url || null;
            });
    },
};

// Guidelines API
export const guidelinesApi = {
    getGuidelines: () => apiClient.get<GuidelineClientsResponse>(API_ENDPOINTS.GUIDELINES),

    getGuideline: (guidelineId: string, version?: number) => {
        const url = version
            ? `${API_ENDPOINTS.GUIDELINE(guidelineId)}?version=${version}`
            : API_ENDPOINTS.GUIDELINE(guidelineId);
        return apiClient.get<GuidelineResponse>(url);
    },

    getGuidelineVersions: (guidelineId: string) =>
        apiClient.get<GuidelineVersionsResponse>(API_ENDPOINTS.GUIDELINE_VERSIONS(guidelineId)),

    updateGuideline: (guidelineId: string, payload: UpdateGuidelineRequest) => {
        const form = new FormData();
        form.append('payload_json', JSON.stringify(payload));
        return apiClient.uploadForm<GuidelineResponse>(API_ENDPOINTS.UPDATE_GUIDELINE(guidelineId), form, 'PUT');
    },

    updateGuidelineYaml: (guidelineId: string, yaml: string) =>
        apiClient.put<GuidelineResponse>(API_ENDPOINTS.UPDATE_GUIDELINE_YAML(guidelineId), { yaml }),

    createGuideline: (guideline: Omit<Guideline, 'versions'>) =>
        apiClient.post<Omit<Guideline, 'versions'>>(API_ENDPOINTS.GUIDELINES, guideline),

    deleteGuideline: (guidelineId: string) =>
        apiClient.delete<void>(API_ENDPOINTS.DELETE_GUIDELINE(guidelineId)),

    updateGuidelineWithAssets: (
        guidelineId: string,
        payload: UpdateGuidelineRequest,
        localFiles: Record<string, File>,
        unfixableRulesFile?: File | null,
    ) => {
        const form = new FormData();
        form.append('payload_json', JSON.stringify(payload));
        for (const [localId, file] of Object.entries(localFiles)) {
            const extFromName = (file.name.split('.').pop() || '').trim();
            const ext = extFromName || (file.type === 'image/jpeg' ? 'jpg' : file.type === 'image/webp' ? 'webp' : 'png');
            // Use Blob to ensure filename is honored across browsers.
            const blob = file.slice(0, file.size, file.type);
            form.append('files', blob, `${localId}.${ext}`);
        }
        if (unfixableRulesFile) {
            form.append('unfixable_rules_file', unfixableRulesFile, unfixableRulesFile.name);
        }
        return apiClient.uploadForm<GuidelineResponse>(API_ENDPOINTS.UPDATE_GUIDELINE(guidelineId), form, 'PUT');
    },

    downloadUnfixableRulesExcel: (guidelineId: string, version?: number) =>
        apiClient.getDownloadBlob(
            API_ENDPOINTS.GUIDELINE_UNFIXABLE_RULES_EXPORT(guidelineId, version)
        ),

    getGuidelineAssetBlob: (filePath: string) => {
        const encoded = encodeURIComponent(filePath);
        return apiClient.getImageBlob(`${API_ENDPOINTS.GUIDELINE_ASSET}?file_path=${encoded}`);
    },
};

// Export all APIs
export const api = {
    dashboard: dashboardApi,
    upload: uploadApi,
    jobs: jobsApi,
    llmUsage: llmUsageApi,
    accuracyMonitoring: accuracyMonitoringApi,
    statusDashboard: statusDashboardApi,
    violationTrend: violationTrendApi,
    violations: violationsApi,
    violation: violationApi,
    guidelines: guidelinesApi,
};

export default api;
